import { useFetcher, useLoaderData, useNavigate, useRevalidator } from '@remix-run/react';
import { useState, useEffect } from 'react';
import { atom } from 'nanostores';
import type { UIMessage } from 'ai';
import { toast } from 'react-toastify';
import { workbenchStore } from '~/lib/stores/workbench';
import {
  getFileActions,
  deleteById,
  getMessages,
  getNextId,
  getUrlId,
  openDatabase,
  runProjectIdMigration,
  setFileActions,
  setMessages,
  setProjectId,
  type Connections,
} from './db';
import { selectionStore } from './selectionStore';
import type { StoreFileAction } from '~/types/actions';
import { githubBranchStore, githubOwnerStore, githubPlatformStore, githubRepoStore } from '~/lib/stores/integrations';
import { type ConnectionData } from '~/services/serviceInterface';
import { updateProjectConnection } from '~/utils/helper';
import * as Sentry from '@sentry/remix';

export interface ChatHistoryItem {
  id: string;
  projectId: string | undefined; // for cloud
  urlId?: string;
  description?: string;
  messages: UIMessage[];
  fileActions: StoreFileAction[];
  timestamp: string;
  netlifyCreds?: {
    netlifyUrl: string;
    netlifySiteId: string;
    netlifyToken: string;
    inspectDeployment: string;
  };
  projectRules?: string;
  techStack?: { framework: string; language: string; packageManager?: string };
  isGitRepo?: boolean;
  connections?: Connections;
  initSource?: string;
}

const persistenceEnabled = !import.meta.env.VITE_DISABLE_PERSISTENCE;

console.log('[projectId] openDatabase called from useChatHistory');

export const db = persistenceEnabled ? await openDatabase() : undefined;

export const chatId = atom<string | undefined>(undefined); // equivalent to projectId in the new schema
export const description = atom<string | undefined>(undefined);
export const urlIdStore = atom<string | undefined>(undefined);

export const projectId = atom<string | undefined>(undefined);

// console.log('description', description.get());

const getData = async () => {
  const { firstAIArtifact } = workbenchStore;

  console.log('[move] getData firstAIArtifact:', firstAIArtifact);

  if (!urlIdStore.get() && firstAIArtifact?.id) {
    const newUrlId = await getUrlId(db as IDBDatabase, firstAIArtifact.id);
    console.log('[useChatHistory] newUrlId:', newUrlId);

    urlIdStore.set(newUrlId);
    navigateChat(newUrlId);
  }

  if (!description.get() && firstAIArtifact?.title) {
    description.set(firstAIArtifact?.title);
  }

  if (!chatId.get()) {
    const nextId = await getNextId(db as IDBDatabase);

    chatId.set(nextId);

    if (!urlIdStore.get()) {
      navigateChat(nextId);
    }
  }
};

export function useChatHistory() {
  selectionStore.setKey('projectName', 'Untitled');

  const navigate = useNavigate();
  const projectsFetcher = useFetcher();

  const loaderData = useLoaderData<{ id?: string; projectId?: string; token?: string }>();
  const { id: mixedId, projectId: mixedProjectId, token } = loaderData;

  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [initialFileActions, setInitialFileActions] = useState<StoreFileAction[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const [showCloudProjectErrorModal, setShowCloudProjectErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { revalidate } = useRevalidator();

  useEffect(() => {
    console.log('[projectId] location of useChatHistory: ', window.location.href);

    if (db && token && !window.location.href.includes('chat/')) {
      console.log('[projectId] Token available in useChatHistory, triggering deferred migration if needed');
      runProjectIdMigration(db, token).then(() => {
        revalidate();
      });

      revalidate();
    }
  }, [token]);

  const createCloudProject = async (
    framework: string,
    language: string,
    packageManager: string,
    initSource: string,
    isGitRepo: boolean,
    token: string,
    urlId: string,
    chatId: string,
    description: string,
  ) => {
    if (!projectId.get() && chatId) {
      console.log('[projects] Conditions met for project upload, preparing data...');

      const [fwork, cssLib] = framework.split('_');
      const techStack = {
        framework: fwork,
        cssLib,
        language,
        packageManager,
      };
      const source = initSource == 'alpha' ? 'alpha' : 'github';

      const valuesArray = [
        {
          ownerId: token,
          currentVersion: null,
          projectName: description || '',
          techStack,
          isGitRepo,
          initSource: source,
          type: 'local',
          urlId,
          idbChatId: chatId,
        },
      ];

      const formData = new FormData();
      formData.set('valuesArray', JSON.stringify({ valuesArray, type: 'create' }));
      projectsFetcher.submit(formData, { method: 'post', action: '/api/projects' });
    }
  };

  useEffect(() => {
    if (!db) {
      setReady(true);

      if (persistenceEnabled) {
        toast.error(`Chat persistence is unavailable`);
      }

      return;
    }

    if (mixedProjectId) {
      projectId.set(mixedProjectId);
    }

    if (mixedId) {
      getMessages(db, mixedId)
        .then((storedMessages) => {
          console.log('[history] storedMessages:', storedMessages);

          if (storedMessages && storedMessages.messages.length > 0) {
            setInitialMessages(storedMessages.messages);
            setInitialFileActions(storedMessages.fileActions);
            urlIdStore.set(storedMessages.urlId);
            description.set(storedMessages.description);
            chatId.set(storedMessages.id);
          } else {
            navigate(`/`, { replace: true });
          }

          setReady(true);
        })
        .catch((error) => {
          /*
           * Sentry.captureException(error, {
           *   extra: {
           *     message: 'Error loading chat history',
           *     mixedId,
           *   },
           * });
           */
          toast.error(error.message);
        });
    }
  }, []);

  useEffect(() => {
    console.log('[projects] projectId from projectsFetcher: ', projectsFetcher.data);

    if (projectsFetcher.data) {
      const { ok, message: pId, error } = projectsFetcher.data as { ok: boolean; message: string; error: string };

      if (ok) {
        projectId.set(pId);

        // url -> /chat/<projectId>/<chatId></chatId>
        const url = new URL(window.location.href);
        url.pathname = urlIdStore.get() ? `/chat/${pId}/${urlIdStore.get()}` : `/chat/${pId}/${chatId.get()}`;
        window.history.replaceState({}, '', url);

        // update projectId in indexedDB
        const updateProjectIdInIDB = async () => {
          if (db) {
            await setProjectId(db as IDBDatabase, chatId.get() as string, pId);
          }
        };

        // after project is created, add github connection, if present
        const updateGithubConnection = async () => {
          const repoName = githubRepoStore.get();
          const ownerName = githubOwnerStore.get();
          const branch = githubBranchStore.get();

          const isGitRepo = repoName && ownerName && branch;
          const pId = projectId.get();

          if (isGitRepo && pId) {
            const connectionData: ConnectionData = {
              connectionType: 'github',
              connectionData: {
                repoName,
                ownerName,
                branch,
              },
            };

            console.log('[projects] projectId from github connection: ', pId);

            await updateProjectConnection(pId, connectionData);
          }
        };

        updateProjectIdInIDB();
        updateGithubConnection();
      } else {
        const deleteChat = async () => {
          if (db) {
            await deleteById(db as IDBDatabase, chatId.get() as string);
          }
        };
        deleteChat();
        Sentry.captureException('Error creating a cloud project', {
          extra: {
            chatId: chatId.get(),
            projectId: pId,
            token,
          },
        });
        setErrorMessage(error || 'Failed to create project. Please try again.');
        setShowCloudProjectErrorModal(true);
      }
    }
  }, [projectsFetcher.data]);

  const handleCloseErrorModal = () => {
    setShowCloudProjectErrorModal(false);
    window.location.href = '/';
  };

  return {
    ready: !mixedId || ready,
    initialMessages,
    initialFileActions,
    showCloudProjectErrorModal,
    errorMessage,
    handleCloseErrorModal,
    storeMessageHistory: async (
      messages: UIMessage[],
      framework: string,
      language: string,
      packageManager: string,
      token: string,
      isGitRepo: boolean,
      initSource: string,
      connections?: Connections,
    ) => {
      if (!db || messages.length === 0) {
        return;
      }

      await getData();

      await createCloudProject(
        framework,
        language,
        packageManager,
        initSource,
        isGitRepo,
        token,
        urlIdStore.get() ?? '',
        chatId.get() ?? '',
        description.get() ?? '',
      );

      await setMessages(
        db as IDBDatabase,
        chatId.get() as string,
        messages,
        framework,
        language,
        packageManager,
        (isGitRepo = isGitRepo || false),
        initSource,
        urlIdStore.get(),
        description.get(),
        connections,
      );
      console.log('[refresh] storeMessageHistory setMessages called:');
    },
    storeFileActions: async (fileActions: StoreFileAction[]) => {
      if (!db || fileActions.length === 0) {
        return;
      }

      await getData();
      await setFileActions(db as IDBDatabase, chatId.get() as string, fileActions);
    },
    getFileActions: async () => {
      if (!db) {
        return [];
      }

      return await getFileActions(db as IDBDatabase, chatId.get() as string);
    },
  };
}

function navigateChat(nextId: string) {
  /**
   * FIXME: Using the intended navigate function causes a rerender for <Chat /> that breaks the app.
   *
   * `navigate(`/chat/${nextId}`, { replace: true });`
   */
  const url = new URL(window.location.href);

  const pid = projectId.get();
  url.pathname = pid ? `/chat/${pid}/${nextId}` : `/chat/${nextId}`;
  window.history.replaceState({}, '', url);
}

export async function storeFileActions(fileActions: StoreFileAction[]) {
  if (!db || fileActions.length === 0) {
    return;
  }

  const repoName = githubRepoStore.get();
  const ownerName = githubOwnerStore.get();
  const branch = githubBranchStore.get();
  const platformDetails = githubPlatformStore.get() || 'github';
  let isGitRepo = false;
  let connections: Connections | undefined;
  let initSource = 'alpha';

  if (repoName && ownerName && branch) {
    initSource = 'github';
    isGitRepo = true;
    connections = {
      [platformDetails]: {
        repoName,
        ownerName,
        branch,
      },
    };
  }

  console.log('connections from useChatHistory', connections, 'initSorce', initSource, isGitRepo);

  await getData();

  await setFileActions(db as IDBDatabase, chatId.get() as string, fileActions, initSource, isGitRepo, connections);
}

export function setDescriptionFromExamplePrompt(title: string) {
  description.set(title);
}
